import { configureStore, combineReducers, createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import localforage from "localforage";
import authReducer, { loginUser, getRefreshToken, logout, logoutAndClearStorage } from "./slices/authSlice";
import workOrdersReducer from "./slices/workOrderSlice";
import workOrderDetailReducer from "./slices/workOrderDetailSlice";
import inspectionTemplatesReducer from "./slices/inspectionTemplatesSlice";
import pendingUploadsQueueReducer from "./slices/pendingUploadsQueue";
import toasterReducer from "./slices/toasterSlice";
import redirectReducer from "./slices/redirectSlice";
import clarityReducer from './slices/claritySlice';
import inboundItemFormReducer from "./slices/inboundItemFormSlice";
import inboundItemsReducer from "./slices/InboundItemsSlice";
import { updateBaseURLsFromTenant, clearTenantIdCache } from "./services/axiosInstance";

const rootReducer = combineReducers({
  auth: authReducer,
  workOrders: workOrdersReducer,
  workOrderDetail: workOrderDetailReducer,
  inspectionTemplates: inspectionTemplatesReducer,
  pendingUploadsQueue: pendingUploadsQueueReducer,
  toaster: toasterReducer,
  redirect: redirectReducer,
  clarity: clarityReducer,
  inboundItemForm: inboundItemFormReducer,
  inboundItems: inboundItemsReducer,
});

const persistConfig = {
  key: "root",
  storage: localforage,
  blacklist: ['auth.error'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create listener middleware for updating API URLs when auth state changes
export const apiUrlListenerMiddleware = createListenerMiddleware();

// Listen for auth actions that change the token
apiUrlListenerMiddleware.startListening({
  matcher: isAnyOf(
    loginUser.fulfilled,
    getRefreshToken.fulfilled,
    logoutAndClearStorage.fulfilled,
    logout.match // Regular action creator uses .match
  ),
  effect: (action) => {
    // Clear cache when token changes
    clearTenantIdCache();
    
    // Update URLs based on new token
    updateBaseURLsFromTenant();
    
    if (import.meta.env.DEV) {
      console.log('🔄 Auth action triggered URL update:', {
        action: action.type,
      });
    }
  },
});

// Also listen for REHYDRATE to handle persisted tokens on app startup
apiUrlListenerMiddleware.startListening({
  type: 'persist/REHYDRATE',
  effect: () => {
    // Clear cache when persisted state is loaded
    clearTenantIdCache();
    
    // Update URLs based on persisted token
    updateBaseURLsFromTenant();
    
    if (import.meta.env.DEV) {
      console.log('🔄 REHYDRATE triggered URL update for persisted token');
    }
  },
});

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>  getDefaultMiddleware({
    thunk: true,
    serializableCheck: {
      ignoredActions: [
        'persist/PERSIST',
        'persist/REHYDRATE',
        'persist/PAUSE',
        'persist/PURGE',
        'persist/REGISTER',
      ],
    },
  }).prepend(apiUrlListenerMiddleware.middleware),
});

export const persistor = persistStore(store);
export default store;

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
